import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { ArrowLeft, Send, Bot, User, Loader2, Sparkles, MessageSquare, Target, RefreshCw, AlertTriangle, Folder, ChevronDown, ChevronRight, Database, BarChart2 } from 'lucide-react';
import DatasetSelector from './analytics/DatasetSelector';
import GuidedQuestions from './analytics/GuidedQuestions';
import OutputRenderer from './analytics/OutputRenderer';
import ReportDownload from './analytics/ReportDownload';

// Use consistent API URL pattern with MRSManagement
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;

const CohortAIChat = () => {
  const { mrsId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // dataset IDs pre-selected in the Upload Data section on the MRS card
  const preDeselectedDatasetIds = location.state?.preDeselectedDatasetIds || [];
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Loading and error states
  const [initializing, setInitializing] = useState(true);
  const [mrsData, setMrsData] = useState(null);
  const [patientCount, setPatientCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMrs, setLoadingMrs] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'not_found', 'auth', 'network', 'server'
  const [retryCount, setRetryCount] = useState(0);
  const [sessionId] = useState(() => {
    const key = `dare_session_${mrsId}_${user?.id}`;
    let stored = localStorage.getItem(key);
    if (!stored) {
      stored = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(key, stored);
    }
    return stored;
  });
  
  // Dynamic prompts from user configuration
  const [purposeCategories, setPurposeCategories] = useState([]);
  const [selectedPurposeIds, setSelectedPurposeIds] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loadingPurposes, setLoadingPurposes] = useState(true);

  // Analytics (Phase 2) state
  const [activeRightTab, setActiveRightTab]             = useState('analytics'); // 'prompts' | 'analytics'
  const [analyticsSession, setAnalyticsSession]         = useState(null);
  const [analyticsSessionStarting, setAnalyticsSessionStarting] = useState(false);
  const [analyticsError, setAnalyticsError]             = useState(null);
  const [analyticsJoinError, setAnalyticsJoinError]     = useState(null);
  const [analyticsDatasetNames, setAnalyticsDatasetNames] = useState([]);
  const [analyticsQueryLoading, setAnalyticsQueryLoading] = useState(false);
  const analyticsInitRef                                  = useRef(false);
  const analyticsDatasetIdsRef                            = useRef(null); // sorted IDs used for current session

  // Feedback state: messageId (string) → 'up' | 'down' | null
  const [feedbackState, setFeedbackState] = useState({});

  // Available datasets for selection before session starts
  const [availableDatasets, setAvailableDatasets]     = useState([]);      // all datasets in MRS
  const [selectedDatasetIds, setSelectedDatasetIds]   = useState(null);    // null = not loaded yet

  // Color palette for categories
  const categoryColors = [
    'bg-blue-100 text-blue-700 hover:bg-blue-200',
    'bg-purple-100 text-purple-700 hover:bg-purple-200',
    'bg-green-100 text-green-700 hover:bg-green-200',
    'bg-orange-100 text-orange-700 hover:bg-orange-200',
    'bg-red-100 text-red-700 hover:bg-red-200',
    'bg-teal-100 text-teal-700 hover:bg-teal-200',
    'bg-pink-100 text-pink-700 hover:bg-pink-200',
    'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
  ];

  // Check if dependencies are ready before fetching
  // Note: This app uses user_id query params for auth (not Bearer tokens)
  const dependenciesReady = useCallback(() => {
    return !!(mrsId && user?.id);
  }, [mrsId, user?.id]);

  // Wait for dependencies before initializing
  useEffect(() => {
    const checkAndInitialize = () => {
      if (dependenciesReady()) {
        setInitializing(false);
      } else {
        // Keep checking every 100ms for up to 3 seconds
        const timeout = setTimeout(() => {
          if (dependenciesReady()) {
            setInitializing(false);
          } else if (retryCount < 30) {
            setRetryCount(prev => prev + 1);
          } else {
            // After 3 seconds, show error
            setInitializing(false);
            if (!mrsId) {
              setError('Medical Record Set ID is missing');
              setErrorType('not_found');
            } else if (!user?.id) {
              setError('User session not loaded. Please log in again.');
              setErrorType('auth');
            }
          }
        }, 100);
        return () => clearTimeout(timeout);
      }
    };

    checkAndInitialize();
  }, [dependenciesReady, retryCount, mrsId, user?.id]);

  // Fetch data once initialized
  useEffect(() => {
    if (!initializing && dependenciesReady()) {
      fetchMrsData();
      fetchUserPurposes();
    }
  }, [initializing, mrsId, user?.id]);

  // Load chat history on mount
  useEffect(() => {
    if (!mrsId || !user?.id) return;
    const loadHistory = async () => {
      try {
        const res = await axios.get(
          `${ANALYTICS_API}/mrs/${mrsId}/chat-history?user_id=${user.id}`
        );
        const loaded = [];

        // Load analytics messages (with charts/tables)
        (res.data.analytics_messages || []).forEach((msg) => {
          loaded.push({
            id: Date.now() + Math.random(),
            role: msg.role,
            content: msg.content || '',
            analyticsData: msg.analyticsData || undefined,
            timestamp: msg.timestamp,
          });
        });

        // Load legacy messages (text only) — only if no analytics messages
        if (loaded.length === 0) {
          (res.data.legacy_messages || []).forEach((msg) => {
            loaded.push({
              id: Date.now() + Math.random(),
              role: msg.role,
              content: msg.content || '',
              timestamp: msg.timestamp,
            });
          });
        }

        // Always reset messages to this MRS's history — even when empty — so
        // switching from an MRS with history to one without never leaves the
        // previous MRS's messages on screen.
        setMessages(loaded);
      } catch (err) {
        console.warn('Could not load chat history:', err);
      }
    };
    loadHistory();
  }, [mrsId, user?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMrsData = async () => {
    // Guard against missing dependencies
    if (!mrsId || !user?.id) {
      console.error('Cannot fetch MRS: missing mrsId or user');
      return;
    }

    try {
      setLoadingMrs(true);
      setError(null);
      setErrorType(null);

      // Fetch MRS data with user_id for proper scoping (same pattern as MRSManagement)
      const response = await axios.get(`${API}/mrs/${mrsId}?user_id=${user.id}`);
      setMrsData(response.data);
      
      // Fetch patient count
      try {
        const patientsResponse = await axios.get(`${API}/mrs/${mrsId}/patients?user_id=${user.id}`);
        setPatientCount(patientsResponse.data?.length || 0);
      } catch (patientErr) {
        // Patient count is not critical, just log and continue
        console.warn('Could not fetch patient count:', patientErr);
        setPatientCount(0);
      }

      setLoadingMrs(false);
    } catch (err) {
      console.error('Error fetching MRS data:', err);
      setLoadingMrs(false);
      
      // Categorize error for appropriate handling
      if (err.response) {
        const status = err.response.status;
        if (status === 404) {
          setError('Medical Record Set not found. It may have been deleted or you may not have access.');
          setErrorType('not_found');
        } else if (status === 401 || status === 403) {
          setError('You do not have permission to access this Medical Record Set. Please log in again.');
          setErrorType('auth');
        } else if (status >= 500) {
          setError('Server error. Please try again in a moment.');
          setErrorType('server');
        } else {
          setError(err.response.data?.detail || 'Failed to load Medical Record Set data');
          setErrorType('server');
        }
      } else if (err.request) {
        // Network error - no response received
        setError('Network error. Please check your connection and try again.');
        setErrorType('network');
      } else {
        setError('An unexpected error occurred. Please try again.');
        setErrorType('server');
      }
    }
  };

  const fetchUserPurposes = async () => {
    try {
      if (!user?.id) return;
      setLoadingPurposes(true);

      // Fetch user's configuration to get selected purpose IDs
      const configResponse = await axios.get(`${API}/setup/configuration?user_id=${user.id}`);
      const selectedIds = configResponse.data?.selected_purposes || [];
      setSelectedPurposeIds(selectedIds);

      // Fetch all purpose categories to get names
      const categoriesResponse = await axios.get(`${API}/setup/purpose-categories`);
      const categories = categoriesResponse.data?.categories || [];
      setPurposeCategories(categories);
      
      // Auto-expand categories that have selected items
      const expanded = {};
      categories.forEach(cat => {
        const hasSelectedItems = cat.items?.some(item => selectedIds.includes(item.id));
        if (hasSelectedItems) {
          expanded[cat.id] = true;
        }
      });
      setExpandedCategories(expanded);
      
    } catch (err) {
      // User purposes are optional, just log
      console.warn('Could not fetch user purposes:', err);
    } finally {
      setLoadingPurposes(false);
    }
  };

  // Get selected purposes grouped by category
  const getSelectedPurposesByCategory = () => {
    const result = [];
    purposeCategories.forEach((category, index) => {
      const selectedItems = (category.items || []).filter(item => 
        selectedPurposeIds.includes(item.id) && !item.is_deleted
      );
      if (selectedItems.length > 0) {
        result.push({
          ...category,
          selectedItems,
          colorClass: categoryColors[index % categoryColors.length]
        });
      }
    });
    return result;
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleRetry = () => {
    setError(null);
    setErrorType(null);
    setRetryCount(0);
    fetchMrsData();
    fetchUserPurposes();
  };

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputValue.trim();
    if (!textToSend || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Route to analytics pipeline when an active session exists — regardless of
    // which right tab is currently visible.  The tab controls only the right panel
    // display; if the user has loaded datasets and a session is live we should
    // always use the analytics pipeline so DuckDB/Parquet is queried correctly.
    if (analyticsSession?.session_id) {
      // Auto-switch to the analytics tab so the user sees the structured result
      setActiveRightTab('analytics');
      setAnalyticsQueryLoading(true);
      try {
        const res = await axios.post(
          `${ANALYTICS_API}/query/chat?user_id=${user.id}`,
          { session_id: analyticsSession.session_id, question: textToSend }
        );
        const analyticsMsg = {
          id: Date.now() + 1,
          role: 'assistant',
          content: '',
          analyticsData: res.data,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, analyticsMsg]);
      } catch (err) {
        const detail = err.response?.data?.detail;
        const errMsg = typeof detail === 'string' ? detail
          : typeof detail === 'object' ? (detail.message || JSON.stringify(detail))
          : 'Analytics query failed. Please try again.';
        const errorMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: errMsg,
          timestamp: new Date().toISOString(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
        setAnalyticsQueryLoading(false);
        inputRef.current?.focus();
      }
      return;
    }

    // Default: text AI (Quick Prompts tab or no analytics session)
    try {
      const response = await axios.post(
        `${API}/mrs/${mrsId}/ai-analysis?user_id=${user.id}`,
        {
          message: textToSend,
          session_id: sessionId,
          response_format: 'text'
        }
      );

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response || response.data.message || 'I apologize, but I could not generate a response.',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      let errorContent = 'Sorry, there was an error processing your request. Please try again.';
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        if (status === 404) {
          errorContent = 'Medical Record Set not found. It may have been deleted or you may not have access.';
        } else if (status === 401 || status === 403) {
          errorContent = 'Authentication error. Please refresh the page and try again.';
        } else if (status === 422) {
          errorContent = 'Invalid request format. Please try rephrasing your question.';
        } else if (status >= 500) {
          errorContent = 'Server error. The AI service may be temporarily unavailable. Please try again in a moment.';
        } else if (detail) {
          if (detail.toLowerCase().includes('no patient') || detail.toLowerCase().includes('no data') || detail.toLowerCase().includes('empty')) {
            errorContent = 'No patient data found in this Medical Record Set to analyze yet. Please add patient records first.';
          } else {
            errorContent = detail;
          }
        }
      } else if (err.request) {
        errorContent = 'Network error. Please check your connection and try again.';
      }
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: errorContent,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleQuickPrompt = (prompt) => {
    handleSendMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /* ── Analytics handlers ─────────────────────────────────────────────── */
  // Auto-start analytics session the first time the Data Analytics tab is opened.
  // Fetches all datasets and excludes any the user deselected on the Upload Data page.
  useEffect(() => {
    if (activeRightTab !== 'analytics') return;
    if (!mrsId || !user?.id) return;

    // If a session is already running, check whether the available datasets have
    // changed since the session started (e.g. the user uploaded a new file).
    // If so, silently reset so the new dataset is included automatically.
    if (analyticsInitRef.current && analyticsDatasetIdsRef.current) {
      (async () => {
        try {
          const dsRes = await axios.get(`${ANALYTICS_API}/mrs/${mrsId}/datasets?user_id=${user.id}`);
          const allIds = (dsRes.data.datasets || []).map(d => d.dataset_id);
          const idsToUse = allIds.filter(id => !preDeselectedDatasetIds.includes(id));
          if ([...idsToUse].sort().join(',') !== analyticsDatasetIdsRef.current) {
            handleRefreshAnalytics(); // datasets changed — restart session
          }
        } catch { /* ignore — don't disrupt an active session on network error */ }
      })();
      return;
    }

    if (analyticsInitRef.current) return;
    analyticsInitRef.current = true;
    setAnalyticsSessionStarting(true);
    setAnalyticsError(null);
    (async () => {
      try {
        // Fetch all datasets and exclude any the user explicitly deselected on the Upload page
        const dsRes = await axios.get(`${ANALYTICS_API}/mrs/${mrsId}/datasets?user_id=${user.id}`);
        const allDatasets = dsRes.data.datasets || [];
        const allIds = allDatasets.map(d => d.dataset_id);
        const idsToUse = allIds.filter(id => !preDeselectedDatasetIds.includes(id));
        if (idsToUse.length === 0) return; // no datasets selected — show empty state

        // Capture friendly names for the datasets being used
        const names = allDatasets
          .filter(d => idsToUse.includes(d.dataset_id))
          .map(d => d.file_name || d.dataset_id);
        setAnalyticsDatasetNames(names);

        const sessRes = await axios.post(
          `${ANALYTICS_API}/sessions/start?user_id=${user.id}`,
          { mrs_id: mrsId, dataset_ids: idsToUse }
        );
        setAnalyticsSession(sessRes.data);
        analyticsDatasetIdsRef.current = [...idsToUse].sort().join(','); // track for change detection
        if (sessRes.data.joinable === false && sessRes.data.join_error) {
          setAnalyticsJoinError(sessRes.data.join_error);
        }

      } catch {
        setAnalyticsError('Failed to start analytics session. Please try again.');
        analyticsInitRef.current = false; // allow retry
      } finally {
        setAnalyticsSessionStarting(false);
      }
    })();
  }, [activeRightTab]); // eslint-disable-line

  const handleFeedback = async (messageId, thumbsUp, messages) => {
    // Prevent double-voting
    if (feedbackState[messageId]) return;

    // Optimistic update
    setFeedbackState(prev => ({ ...prev, [messageId]: thumbsUp ? 'up' : 'down' }));

    // Find the question: the user message immediately before this assistant message
    const msgIndex = messages.findIndex(m => m.id === messageId);
    const questionText = msgIndex > 0 && messages[msgIndex - 1].role === 'user'
      ? messages[msgIndex - 1].content
      : '';

    if (!analyticsSession?.session_id) return;
    try {
      await axios.post(
        `${ANALYTICS_API}/query/feedback?user_id=${user.id}`,
        {
          session_id: analyticsSession.session_id,
          message_id: String(messageId),
          question:   questionText,
          thumbs_up:  thumbsUp,
        }
      );
    } catch (_err) {
      // Silently ignore — feedback is best-effort
    }
  };

  const handleRefreshAnalytics = () => {
    analyticsInitRef.current = false;
    analyticsDatasetIdsRef.current = null;
    setAnalyticsSession(null);
    setAnalyticsError(null);
    setAnalyticsJoinError(null);
    setAnalyticsDatasetNames([]);
    setAnalyticsSessionStarting(false);
    setAvailableDatasets([]);
    setSelectedDatasetIds(null);
    // setActiveRightTab('prompts');  // Quick Prompts tab disabled
    setTimeout(() => setActiveRightTab('analytics'), 50);
  };

  const handleAnalyticsResult = (response, questionText) => {
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: questionText,
      timestamp: new Date().toISOString(),
    };
    const assistantMsg = {
      id: Date.now() + 1,
      role: 'assistant',
      content: '',
      analyticsData: response,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
  };

  /**
   * Called when the user clicks "Yes, use this" or "No, answer as typed" on a
   * partial-match confirmation card.
   * confirmed=true  → execute the suggested pre-built question
   * confirmed=false → re-run the original question through novel agent
   */
  const handleConfirmMatch = async (confirmed, partialMatchData) => {
    if (!analyticsSession?.session_id) return;
    setAnalyticsQueryLoading(true);
    try {
      const res = await axios.post(
        `${ANALYTICS_API}/query/confirm?user_id=${user.id}`,
        {
          session_id: analyticsSession.session_id,
          confirmed,
          suggested_question_id: partialMatchData.suggested_question_id,
          original_question: partialMatchData.original_question,
        }
      );
      // Replace the confirmation card with the real result
      const questionText = confirmed
        ? partialMatchData.suggested_question
        : partialMatchData.original_question;
      const assistantMsg = {
        id: Date.now(),
        role: 'assistant',
        content: '',
        analyticsData: res.data,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => {
        // Remove the last assistant message (the confirmation card) and append result
        const withoutLast = [...prev];
        for (let i = withoutLast.length - 1; i >= 0; i--) {
          if (withoutLast[i].role === 'assistant' &&
              withoutLast[i].analyticsData?.action === 'confirm') {
            withoutLast.splice(i, 1);
            break;
          }
        }
        return [...withoutLast, assistantMsg];
      });
    } catch (err) {
      const errMsg = err.response?.data?.detail?.message || 'Confirmation failed. Please try again.';
      setMessages((prev) => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: errMsg,
        isError: true,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setAnalyticsQueryLoading(false);
    }
  };

  const formatMessageContent = (content) => {
    // Simple markdown-like formatting
    return content.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < content.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Show initializing state
  if (initializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loadingMrs && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Medical Record Set...</p>
        </div>
      </div>
    );
  }

  // Show error state with retry option (don't force back)
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {errorType === 'not_found' ? 'Record Not Found' : 
             errorType === 'auth' ? 'Authentication Issue' :
             errorType === 'network' ? 'Connection Error' : 'Error'}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {/* Show retry for transient errors */}
            {(errorType === 'network' || errorType === 'server') && (
              <button
                onClick={handleRetry}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            )}
            
            {/* Show login redirect for auth issues */}
            {errorType === 'auth' && (
              <button
                onClick={() => navigate('/login')}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Log In
              </button>
            )}
            
            {/* Always show back option */}
            <button
              onClick={() => navigate('/mrs')}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Medical Record Sets
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fills the full-bleed shell (ProtectedRoute fullBleed): on large screens it
  // occupies all space under the navbar with internal scrolling — giving the
  // analysis area maximum room; on small screens it flows so the page scrolls.
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-gray-50 lg:overflow-hidden">
      {/* Header Card - Dashboard Style */}
      <div className="px-4 pt-3 pb-2 flex-shrink-0">
        <div 
          className="rounded-xl shadow-lg text-white p-4"
          style={{ 
            background: 'linear-gradient(135deg, var(--theme-primary, #2563eb) 0%, var(--theme-primary-hover, #1d4ed8) 100%)' 
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/mrs')}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <h1 className="text-xl font-bold">AI Data Analysis</h1>
                </div>
                <p className="text-white/80 text-sm mt-0.5">
                  Analyzing: <span className="font-semibold text-white">{mrsData?.name}</span>
                  <span className="mx-2">•</span>
                  <span>{patientCount} patients</span>
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 bg-white/20 rounded-lg px-3 py-1.5">
              <Bot className="w-4 h-4" />
              <span className="text-xs font-medium">AI Assistant</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout (stacks on small screens) */}
      <div className="flex-1 px-2 sm:px-4 pb-3 flex flex-col lg:flex-row gap-3 lg:overflow-hidden min-h-0">
        {/* Left Column - Chat Area */}
        <div className="flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-0 min-w-0 max-lg:h-[60vh]">
          {/* Messages Container - scrollable */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0"
          >
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  Start a Conversation
                </h3>
                <p className="text-gray-500 text-sm max-w-md">
                  Ask questions about your Medical Record Set data, or use the quick prompts on the right to get started.
                </p>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                    <div
                      className={`rounded-xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'max-w-[80%] bg-blue-600 text-white'
                          : message.analyticsData
                          ? 'flex-1 min-w-0 bg-white border border-gray-200 shadow-sm'
                          : message.isError
                          ? 'max-w-[80%] bg-red-50 text-red-800 border border-red-200'
                          : 'max-w-[80%] bg-gray-100 text-gray-800'
                      }`}
                    >
                      {message.analyticsData ? (
                        <div>
                          <OutputRenderer
                            response={message.analyticsData}
                            onConfirm={handleConfirmMatch}
                          />
                          {/* Thumbs feedback — only for successful data responses */}
                          {!message.analyticsData.action && !message.analyticsData.error && (
                            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
                              <span className="text-xs text-gray-400">Was this helpful?</span>
                              <button
                                onClick={() => handleFeedback(message.id, true, messages)}
                                disabled={!!feedbackState[message.id]}
                                title="Helpful"
                                className={`p-1 rounded transition-colors ${
                                  feedbackState[message.id] === 'up'
                                    ? 'text-green-600 bg-green-50'
                                    : feedbackState[message.id]
                                    ? 'text-gray-300 cursor-default'
                                    : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                                }`}
                              >
                                👍
                              </button>
                              <button
                                onClick={() => handleFeedback(message.id, false, messages)}
                                disabled={!!feedbackState[message.id]}
                                title="Not helpful"
                                className={`p-1 rounded transition-colors ${
                                  feedbackState[message.id] === 'down'
                                    ? 'text-red-500 bg-red-50'
                                    : feedbackState[message.id]
                                    ? 'text-gray-300 cursor-default'
                                    : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                                }`}
                              >
                                👎
                              </button>
                              {feedbackState[message.id] && (
                                <span className="text-xs text-gray-400 ml-1">Thanks for your feedback</span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {formatMessageContent(message.content)}
                        </div>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="bg-gray-100 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        <span className="text-sm text-gray-600">Analyzing your data...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Fixed Input Area - sticky at bottom */}
          <div className="border-t border-gray-200 p-3 bg-white flex-shrink-0">
            {/* Pipeline indicator — shows which backend handles the next message */}
            {analyticsSession?.session_id ? (
              <div className="flex items-center gap-1.5 mb-2">
                <Database className="w-3 h-3 text-blue-500" />
                <span className="text-xs text-blue-600 font-medium">
                  Data Analytics active — questions will query your selected dataset
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 mb-2">
                <Bot className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-400">
                  AI assistant — open the Data Analytics tab to query your datasets
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask a question about your data..."
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                disabled={isLoading}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className="px-3 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                style={{ minWidth: '44px' }}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Data Analytics (full width when stacked on small screens) */}
        <div className="w-full lg:w-72 flex-shrink-0 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col min-h-0 max-lg:h-[50vh]">
          {/* Tab header */}
          <div className="flex border-b border-gray-200 flex-shrink-0">
            {/* Quick Prompts tab disabled
            <button
              onClick={() => setActiveRightTab('prompts')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                activeRightTab === 'prompts'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Quick Prompts
            </button>
            */}
            <button
              onClick={() => setActiveRightTab('analytics')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                activeRightTab === 'analytics'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BarChart2 className="w-3.5 h-3.5" />
              Data Analytics
            </button>
          </div>

          {/* ── Quick Prompts tab — disabled ──────────────────────────── */}
          {/* {activeRightTab === 'prompts' && (
            <>
              <div className="p-3 border-b border-gray-100 flex-shrink-0">
                <p className="text-xs text-gray-500">Based on your data usage settings</p>
              </div>
              ... Quick Prompts content removed ...
            </>
          )} */}

          {/* ── Data Analytics tab ───────────────────────────────────── */}
          {activeRightTab === 'analytics' && (
            <div className="flex-1 overflow-y-auto p-3 min-h-0 flex flex-col gap-3">
              {analyticsSessionStarting ? (
                /* Loading datasets or starting session */
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <p className="text-xs text-gray-500 text-center">
                    {selectedDatasetIds ? 'Starting analytics session…' : 'Loading datasets…'}
                  </p>
                </div>
              ) : analyticsError ? (
                /* Error state */
                <div className="flex flex-col items-center gap-2 py-6">
                  <p className="text-xs text-red-500 text-center">{analyticsError}</p>
                  <button onClick={handleRefreshAnalytics} className="text-xs text-blue-600 hover:underline">
                    Retry
                  </button>
                </div>
              ) : !analyticsSession && selectedDatasetIds !== null && availableDatasets.length === 0 ? (
                /* No datasets uploaded yet */
                <div className="text-center py-6 px-2">
                  <Database className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 font-medium mb-1">No datasets available</p>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Upload a file from the MRS card first, then come back here.
                  </p>
                </div>
              ) : !analyticsSession && selectedDatasetIds !== null ? (
                /* Dataset selection UI */
                <div className="flex flex-col gap-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Select datasets to analyse
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {availableDatasets.map(ds => (
                      <label
                        key={ds.dataset_id}
                        className="flex items-start gap-2.5 p-2.5 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-blue-600 shrink-0"
                          checked={selectedDatasetIds.includes(ds.dataset_id)}
                          onChange={() => {
                            setSelectedDatasetIds(prev =>
                              prev.includes(ds.dataset_id)
                                ? prev.filter(id => id !== ds.dataset_id)
                                : [...prev, ds.dataset_id]
                            );
                          }}
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate" title={ds.file_name}>
                            {ds.file_name || ds.dataset_id}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {ds.file_type === 'pdf' ? '📄 PDF' : `📊 ${(ds.row_count || 0).toLocaleString()} rows · ${ds.column_count || 0} cols`}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={handleStartAnalyticsSession}
                    disabled={!selectedDatasetIds || selectedDatasetIds.length === 0}
                    className="w-full py-2 px-4 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Start Analysis ({selectedDatasetIds?.length || 0} selected)
                  </button>
                </div>
              ) : !analyticsSession ? (
                /* Fallback spinner */
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              ) : (
                /* Session active — show dataset badge + suggested questions only */
                <>
                  {/* Dataset badge — disabled */}

                  {/* Join warning (Gap 2) */}
                  {analyticsJoinError && (
                    <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-xs text-amber-900 shrink-0">
                      <p className="font-semibold mb-0.5">Cross-dataset joins unavailable</p>
                      <p className="leading-relaxed">{analyticsJoinError}</p>
                    </div>
                  )}

                  {/* Analytics query loading indicator (shown when freeform chat is routed to analytics) */}
                  {analyticsQueryLoading && (
                    <div className="flex items-center gap-1.5 text-xs text-blue-600 shrink-0 px-1">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Running analytics query…</span>
                    </div>
                  )}

                  <GuidedQuestions
                    questions={analyticsSession.guided_questions || []}
                    datasetId={analyticsSession.dataset_ids?.[0]}
                    mrsId={mrsId}
                    userId={user?.id || ''}
                    sessionId={analyticsSession.session_id}
                    onResult={handleAnalyticsResult}
                  />

                  {/* Download Report — visible once the user has analytics messages */}
                  <ReportDownload
                    sessionId={analyticsSession.session_id}
                    mrsId={mrsId}
                    userId={user?.id || ''}
                    hasResults={messages.some(m => m.analyticsData)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CohortAIChat;
